import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import styles from './editable-grid.module.scss';

const MultiSelectCell = ({ row, column, options, updateRow }) => {
  // Normalize value to array of objects with 'name' property
  const value = (row.original[column.id] || []).map((item) => {
    const name = typeof item === 'object' && item ? item.name || item : item;
    return options.find((opt) => opt.name === name) || { name };
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectRef = useRef(null);

  const handleChange = (selectedOptions) => {
    const newValue = (selectedOptions || []).map((opt) => ({ name: opt.name }));
    updateRow(row.index, column.id, newValue);
    setIsMenuOpen(newValue.length < options.length);
  };

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      color: '#fff',
      boxShadow: 'none',
      borderRadius: '4px',
      minHeight: '36px',
      width: '100%',
      padding: '2px',
      '&:hover': {
        borderColor: 'transparent',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#333',
      color: '#fff',
      width: '100%',
      borderRadius: '4px',
      marginTop: '2px',
      border: 'none',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      border: 'none',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#E9E9E9' : state.isFocused ? '#444' : 'transparent',
      color: state.isSelected ? '#1A1A1A' : '#fff',
      padding: '8px',
      cursor: 'pointer',
      border: 'none',
      '&:hover': {
        backgroundColor: state.isSelected ? '#E9E9E9' : '#444',
        borderColor: 'transparent',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#555',
      margin: '2px',
      border: 'none',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
      padding: '2px 4px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      padding: '2px',
      border: 'none',
      '&:hover': {
        backgroundColor: '#777',
        color: '#fff',
        borderColor: 'transparent',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#aaa',
    }),
    input: (provided) => ({
      ...provided,
      color: '#fff',
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      flexWrap: 'wrap',
      padding: '2px',
      minHeight: '30px',
      overflow: 'visible',
    }),
  };

  const CustomOption = ({ data, isSelected, innerRef, innerProps }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#E9E9E9' : 'transparent',
      }}
    >
      <div
        style={{
          width: '18px',
          height: '18px',
          border: isSelected ? 'none' : '1px solid #606264',
          borderRadius: '4px',
          marginRight: '8px',
          backgroundColor: isSelected ? '#1A1A1A' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isSelected && (
          <span
            style={{
              color: '#E9E9E9',
              fontSize: '14px',
              lineHeight: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✓
          </span>
        )}
      </div>
      {data.name}
    </div>
  );

  return (
    <div style={{ width: '100%' }} ref={selectRef}>
      <Select
        isMulti
        options={options}
        value={value}
        onChange={handleChange}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.name}
        styles={customStyles}
        className={styles.multiSelect}
        placeholder="Select..."
        components={{ Option: CustomOption }}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        menuIsOpen={isMenuOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
      />
    </div>
  );
};

export default MultiSelectCell;